import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ButtonComponent,
  ConfirmDialogService,
  DropdownOption,
  ToastService,
} from '@dental-ui/ui';
import { LucidePlus, LucideRefreshCw, LucideTrash2, LucideX } from '@lucide/angular';
import { catchError, forkJoin, of } from 'rxjs';
import {
  PortalStaticFilesApiService,
  StaticFileItem,
} from '../data-access/portal-static-files-api.service';

export const STATIC_FILE_TYPES: DropdownOption[] = [
  { value: 2, label: 'Crown JS' },
  { value: 3, label: 'Crown Wasm' },
  { value: 4, label: 'Veneer JS' },
  { value: 5, label: 'Veneer Wasm' },
  { value: 8, label: 'Bar JS' },
  { value: 9, label: 'Bar Wasm' },
  { value: 15, label: 'Viewer JS' },
  { value: 16, label: 'Viewer Wasm' },
];

interface SelectedUploadFile {
  file: File;
  type: number | null;
  typeLabel: string;
}

/** Dosya adından statik dosya türünü çıkarır (örn. crown_app.js → Crown JS). */
function detectStaticFileType(fileName: string): number | null {
  const lower = fileName.toLowerCase();
  const isJs = lower.endsWith('.js');
  const isWasm = lower.endsWith('.wasm');
  if (!isJs && !isWasm) return null;

  if (lower.includes('crown')) return isJs ? 2 : 3;
  if (lower.includes('veneer')) return isJs ? 4 : 5;
  if (lower.includes('bar')) return isJs ? 8 : 9;
  if (lower.includes('viewer')) return isJs ? 15 : 16;
  return null;
}

function typeLabel(type: number | null): string {
  if (type == null) return 'Tanınamadı';
  const found = STATIC_FILE_TYPES.find((f) => f.value === type);
  return found ? String(found.label) : `(${type})`;
}

@Component({
  selector: 'lib-portal-static-files',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    LucidePlus,
    LucideRefreshCw,
    LucideTrash2,
    LucideX,
  ],
  templateUrl: './portal-static-files.component.html',
  styleUrl: './portal-static-files.component.scss',
})
export class PortalStaticFilesComponent implements OnInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  private readonly api = inject(PortalStaticFilesApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<StaticFileItem[]>([]);
  readonly loading = signal(false);
  readonly uploading = signal(false);
  readonly showDialog = signal(false);
  readonly selectedFiles = signal<SelectedUploadFile[]>([]);

  ngOnInit(): void {
    this.loadFiles();
  }

  loadFiles(): void {
    this.loading.set(true);
    this.api.loadStaticFiles().subscribe({
      next: (data) => {
        this.loading.set(false);
        this.items.set(data);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Dosyalar yüklenirken hata oluştu');
      },
    });
  }

  getTypeName(type?: number): string {
    return typeLabel(type ?? null);
  }

  openUploadDialog(): void {
    this.selectedFiles.set([]);
    this.showDialog.set(true);
  }

  closeDialog(): void {
    this.showDialog.set(false);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.selectedFiles.set(
      files.map((file) => {
        const type = detectStaticFileType(file.name);
        return { file, type, typeLabel: typeLabel(type) };
      })
    );
  }

  removeFile(index: number): void {
    this.selectedFiles.update((list) => list.filter((_, i) => i !== index));
    if (this.selectedFiles().length === 0 && this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  clearFiles(): void {
    this.selectedFiles.set([]);
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  canUpload(): boolean {
    const files = this.selectedFiles();
    return files.length > 0 && files.every((f) => f.type != null);
  }

  hasUnrecognized(): boolean {
    return this.selectedFiles().some((f) => f.type == null);
  }

  upload(): void {
    const files = this.selectedFiles().filter((f) => f.type != null);
    if (files.length === 0) return;

    this.uploading.set(true);
    forkJoin(
      files.map((item) =>
        this.api.uploadStaticFile(item.file, item.type!).pipe(
          catchError(() => of({ error: true as const, name: item.file.name }))
        )
      )
    ).subscribe({
      next: (results) => {
        this.uploading.set(false);
        const failed = results.filter(
          (r): r is { error: true; name: string } =>
            !!r && typeof r === 'object' && 'error' in r && r.error === true
        );
        const successCount = results.length - failed.length;

        if (failed.length === 0) {
          this.toast.success(
            successCount === 1
              ? 'Dosya başarıyla yüklendi'
              : `${successCount} dosya başarıyla yüklendi`
          );
          this.closeDialog();
          this.loadFiles();
          return;
        }

        if (successCount > 0) {
          this.toast.error(
            `${successCount} dosya yüklendi, ${failed.length} başarısız: ${failed.map((f) => f.name).join(', ')}`
          );
          this.loadFiles();
        } else {
          this.toast.error('Dosyalar yüklenirken hata oluştu');
        }
      },
      error: () => {
        this.uploading.set(false);
        this.toast.error('Dosyalar yüklenirken hata oluştu');
      },
    });
  }

  deleteFile(item: StaticFileItem): void {
    const name = item.description
      ? `${item.description}.${item.code}`
      : item.id;

    this.confirmDialog
      .confirm({
        title: 'Dosyayı Sil',
        message: `"${name}" dosyasını silmek istediğinizden emin misiniz?`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deleteStaticFile(item.id).subscribe({
          next: () => {
            this.toast.success('Dosya başarıyla silindi');
            this.loadFiles();
          },
          error: () => {
            this.toast.error('Dosya silinirken hata oluştu');
          },
        });
      });
  }
}
